// lib/widgets/rows-model.js
// ═══════════════════════════════════════════════════════════
// ROWS MODEL — row-based dashboard layout
// ═══════════════════════════════════════════════════════════
// Tim's request: widgets live in rows, each row's widgets share
// the row width proportionally. 1/2/3/4 widgets per row → auto
// 100/50-50/33-33-33/25x4 by default. User can adjust via a
// drag divider between widgets in the same row.
//
// Data shape (stored in workstations.config.tab_config.dashboard.rows):
//   [
//     { id: 'r1', widgets: [{ key: 'jarvis_briefing', share: 12 }] },
//     { id: 'r2', widgets: [
//         { key: 'macro_gate', share: 6 },
//         { key: 'force_tracker', share: 6 }
//     ]}
//   ]
//
// Each row's widgets' shares sum to 12. "share" is a 12-col grid
// fraction (6 = half-width, 4 = third-width).
//
// Pure functions here — no React, no DB, no IO.
// ═══════════════════════════════════════════════════════════

import { getWidget } from './registry';

export const ROW_TOTAL_SHARE = 12;
export const MIN_WIDGET_SHARE = 3;       // no widget smaller than 1/4 row
export const MAX_WIDGETS_PER_ROW = 4;    // enforced — 4 widgets = 3/3/3/3 min

/** Generate a new row id. */
export function newRowId() {
  return `r_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

// ─── MIGRATION: old sections + layout → rows ──────────────
/**
 * Convert a legacy sections[] + layout[] (react-grid-layout
 * format) into the rows model. Widgets with similar y values
 * get grouped into the same row; within a row, widgets are
 * ordered by x. Shares are computed from w values.
 */
export function migrateLayoutToRows(sections = [], legacyLayout = []) {
  if (!legacyLayout.length) {
    return sectionsToDefaultRows(sections);
  }

  // Build a map from key → layout item
  const layoutByKey = {};
  for (const item of legacyLayout) {
    if (item && item.i) layoutByKey[item.i] = item;
  }

  // Items that exist in sections (prefer sections order for unknown-y widgets)
  const items = sections
    .map((key) => layoutByKey[key] || { i: key, x: 0, y: 0, w: 12, h: 6 })
    .filter(Boolean);

  // Group by y — widgets with overlapping y ranges go into the same row
  items.sort((a, b) => (a.y || 0) - (b.y || 0) || (a.x || 0) - (b.x || 0));
  const rowBuckets = [];
  for (const item of items) {
    const y = item.y || 0;
    let bucket = rowBuckets.find((b) => Math.abs(b.y - y) <= 1);
    if (!bucket) {
      bucket = { y, items: [] };
      rowBuckets.push(bucket);
    }
    bucket.items.push(item);
  }

  return rowBuckets.map((b) => ({
    id: newRowId(),
    widgets: balanceShares(b.items.map((item) => ({
      key: item.i,
      share: Math.max(MIN_WIDGET_SHARE, Math.min(ROW_TOTAL_SHARE, item.w || ROW_TOTAL_SHARE)),
    }))),
  }));
}

/**
 * Default rows layout when only sections[] exists (no layout).
 * One widget per row (full width) — simple, deterministic.
 */
export function sectionsToDefaultRows(sections = []) {
  return (sections || []).map((key) => ({
    id: newRowId(),
    widgets: [{ key, share: ROW_TOTAL_SHARE }],
  }));
}

// ─── BALANCE ──────────────────────────────────────────────
/**
 * Normalize widget shares so they sum to ROW_TOTAL_SHARE.
 *
 * Behavior:
 * 1. Any widget with share<=0 (newly inserted) → even redistribute across ALL
 * 2. Already-balanced (sum=12 + all >= min) → unchanged
 * 3. Proportional rescale otherwise, clamped to MIN_WIDGET_SHARE.
 */
export function balanceShares(widgets = []) {
  if (!widgets.length) return [];
  const n = widgets.length;

  // 1) Any widget with share 0 means it was just inserted → even split
  const hasZero = widgets.some((w) => !w.share || w.share <= 0);
  if (hasZero) return evenlyDistribute(widgets);

  const totalCurrent = widgets.reduce((s, w) => s + (w.share || 0), 0);

  if (totalCurrent === 0 || !isFinite(totalCurrent)) {
    return evenlyDistribute(widgets);
  }

  // 2) Already sums to total + all meet min share
  if (totalCurrent === ROW_TOTAL_SHARE && widgets.every((w) => w.share >= MIN_WIDGET_SHARE)) {
    return widgets;
  }

  // 3) Proportional scale, clamp to mins, fix rounding drift
  const scaled = widgets.map((w) => ({
    ...w,
    share: Math.max(MIN_WIDGET_SHARE, Math.round((w.share / totalCurrent) * ROW_TOTAL_SHARE)),
  }));
  const scaledTotal = scaled.reduce((s, w) => s + w.share, 0);
  const diff = ROW_TOTAL_SHARE - scaledTotal;
  if (diff !== 0 && scaled.length > 0) {
    scaled[scaled.length - 1].share += diff;
    if (scaled[scaled.length - 1].share < MIN_WIDGET_SHARE) {
      scaled[scaled.length - 1].share = MIN_WIDGET_SHARE;
    }
  }
  return scaled;
}

/** Pure even split — 2 → 6/6, 3 → 4/4/4, 4 → 3/3/3/3. */
function evenlyDistribute(widgets) {
  const n = widgets.length;
  const equal = Math.floor(ROW_TOTAL_SHARE / n);
  const remainder = ROW_TOTAL_SHARE - (equal * n);
  return widgets.map((w, i) => ({
    ...w,
    share: equal + (i < remainder ? 1 : 0),
  }));
}

// ─── MUTATIONS ────────────────────────────────────────────
/**
 * Add a widget to the rows. Strategy: append as a new full-width row.
 * User can drag it to join another row later.
 */
export function addWidget(rows, widgetKey, options = {}) {
  const existingIndex = findWidget(rows, widgetKey);
  if (existingIndex) return rows;

  const meta = getWidget(widgetKey);
  const desiredShare = options.share || meta?.default_layout?.w || ROW_TOTAL_SHARE;

  const newRow = {
    id: newRowId(),
    widgets: [{ key: widgetKey, share: Math.min(ROW_TOTAL_SHARE, desiredShare) }],
  };
  return [...rows, newRow];
}

/**
 * Remove a widget from the rows. If it was the only widget in its
 * row, remove the row. Otherwise rebalance the row.
 */
export function removeWidget(rows, widgetKey) {
  const out = [];
  for (const row of rows) {
    const remaining = row.widgets.filter((w) => w.key !== widgetKey);
    if (remaining.length === 0) continue; // drop the empty row
    if (remaining.length === row.widgets.length) {
      out.push(row); // unchanged
    } else {
      out.push({ ...row, widgets: balanceShares(remaining) });
    }
  }
  return out;
}

/**
 * Insert a widget into a specific row at a given position.
 * Other widgets in that row rebalance.
 */
export function insertInRow(rows, widgetKey, rowId, position = -1) {
  const rowsWithoutWidget = removeWidget(rows, widgetKey);
  return rowsWithoutWidget.map((row) => {
    if (row.id !== rowId) return row;
    if (row.widgets.length >= MAX_WIDGETS_PER_ROW) {
      // Row full — create a new row below instead
      // (caller should detect this; we gracefully no-op here)
      return row;
    }
    const widgets = [...row.widgets];
    const idx = position < 0 || position > widgets.length ? widgets.length : position;
    widgets.splice(idx, 0, { key: widgetKey, share: 0 });
    return { ...row, widgets: balanceShares(widgets) };
  });
}

/**
 * Move a widget from its current row to a new row at a given index.
 */
export function moveToNewRow(rows, widgetKey, insertRowIndex = -1) {
  const rowsWithoutWidget = removeWidget(rows, widgetKey);
  const newRow = {
    id: newRowId(),
    widgets: [{ key: widgetKey, share: ROW_TOTAL_SHARE }],
  };
  const idx = insertRowIndex < 0 || insertRowIndex > rowsWithoutWidget.length ? rowsWithoutWidget.length : insertRowIndex;
  return [
    ...rowsWithoutWidget.slice(0, idx),
    newRow,
    ...rowsWithoutWidget.slice(idx),
  ];
}

/**
 * Adjust the share between two adjacent widgets in the same row.
 * Used by the drag divider. delta is in share units (positive = left grows).
 */
export function adjustShareBetween(rows, rowId, leftKey, rightKey, delta) {
  return rows.map((row) => {
    if (row.id !== rowId) return row;
    const widgets = row.widgets.map((w) => ({ ...w }));
    const leftIdx = widgets.findIndex((w) => w.key === leftKey);
    const rightIdx = widgets.findIndex((w) => w.key === rightKey);
    if (leftIdx < 0 || rightIdx < 0 || Math.abs(leftIdx - rightIdx) !== 1) return row;
    const left = widgets[leftIdx];
    const right = widgets[rightIdx];
    const newLeft = Math.max(MIN_WIDGET_SHARE, Math.min(ROW_TOTAL_SHARE - MIN_WIDGET_SHARE, left.share + delta));
    const actualDelta = newLeft - left.share;
    const newRight = right.share - actualDelta;
    if (newRight < MIN_WIDGET_SHARE) return row;
    widgets[leftIdx] = { ...left, share: newLeft };
    widgets[rightIdx] = { ...right, share: newRight };
    return { ...row, widgets };
  });
}

/**
 * Reorder rows by moving from one index to another.
 */
export function reorderRows(rows, fromIdx, toIdx) {
  if (fromIdx === toIdx) return rows;
  const out = [...rows];
  const [moved] = out.splice(fromIdx, 1);
  out.splice(toIdx, 0, moved);
  return out;
}

// ─── QUERIES ──────────────────────────────────────────────
/** Find a widget in the rows. Returns { rowIndex, widgetIndex } or null. */
export function findWidget(rows, widgetKey) {
  for (let r = 0; r < rows.length; r++) {
    const idx = rows[r].widgets.findIndex((w) => w.key === widgetKey);
    if (idx >= 0) return { rowIndex: r, widgetIndex: idx };
  }
  return null;
}

/** Flat list of widget keys in visual order (for quick lookup). */
export function widgetKeysInRows(rows) {
  return rows.flatMap((r) => r.widgets.map((w) => w.key));
}

/** Validate + sanitize rows before persisting. */
export function sanitizeRows(rows) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    if (!row || !Array.isArray(row.widgets)) continue;
    const widgets = [];
    for (const w of row.widgets) {
      if (!w || typeof w.key !== 'string') continue;
      if (seen.has(w.key)) continue;       // dedup across rows
      seen.add(w.key);
      widgets.push({
        key: w.key,
        share: Math.max(MIN_WIDGET_SHARE, Math.min(ROW_TOTAL_SHARE, parseInt(w.share, 10) || MIN_WIDGET_SHARE)),
      });
      if (widgets.length >= MAX_WIDGETS_PER_ROW) break;
    }
    if (widgets.length === 0) continue;
    out.push({
      id: typeof row.id === 'string' ? row.id : newRowId(),
      widgets: balanceShares(widgets),
    });
  }
  return out;
}
